import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, X, Lightning, Star, Crown } from "@phosphor-icons/react";

export default function Pricing() {
  const [tiers, setTiers] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/config").then(({ data }) => setTiers(data.tiers));
  }, []);

  if (!tiers) return <div className="min-h-screen flex items-center justify-center text-ink-dim">LOADING…</div>;

  const onChoose = (key) => {
    if (!user) {
      navigate(`/auth?mode=signup&plan=${key}`);
    } else {
      navigate(`/checkout?plan=${key}`);
    }
  };

  const order = ["standard", "intermediate", "pro"];
  const meta = {
    standard: { tag: "ENTRY", icon: Lightning, color: "border-zinc-800", chip: "text-ink-mute" },
    intermediate: { tag: "MOST PICKED", icon: Star, color: "border-brand-red", chip: "text-brand-red" },
    pro: { tag: "PRO STUDIO", icon: Crown, color: "border-brand-purple", chip: "text-brand-purple" },
  };

  return (
    <div className="relative" data-testid="pricing-page">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="text-[10px] tracking-[0.4em] uppercase text-brand-red mb-4">◢ SUBSCRIPTIONS</div>
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
            CHOOSE YOUR <span className="text-brand-red neon-red">WEAPON.</span>
          </h1>
          <p className="text-ink-mute text-sm mt-6 max-w-xl mx-auto">
            All plans renew monthly. Cancel anytime. Integrated with Square — your card details
            never touch our servers. Built for FL Studio.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
          {order.map((key) => {
            const t = tiers[key];
            const m = meta[key];
            const Icon = m.icon;
            return (
              <div
                key={key}
                data-testid={`pricing-card-${key}`}
                className={`bg-bg-surface p-8 lg:p-10 flex flex-col relative group ${key === "intermediate" ? "ring-1 ring-brand-red" : ""}`}
              >
                {key === "intermediate" && (
                  <div className="absolute -top-3 right-6 bg-brand-red text-black text-[9px] tracking-[0.3em] uppercase font-bold px-3 py-1 clip-tag">
                    BEST VALUE
                  </div>
                )}
                <div className="flex items-center gap-2 mb-6">
                  <Icon size={16} weight="bold" className={m.chip} />
                  <span className={`text-[10px] tracking-[0.3em] uppercase ${m.chip}`}>{m.tag}</span>
                </div>
                <div className="font-display font-black uppercase tracking-tighter text-3xl mb-4">
                  {t.name}
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display font-black text-6xl tracking-tighter">
                    {t.price_display}
                  </span>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-ink-dim">/month</span>
                </div>
                <div className="mt-2 text-[10px] tracking-[0.3em] uppercase text-ink-dim">
                  {t.ai_credits_daily === 9999 ? "Unlimited" : `${t.ai_credits_daily} AI generations / day`}
                </div>

                <div className="my-8 h-px bg-zinc-900" />

                <ul className="space-y-3 mb-10">
                  {t.features.map((f, i) => (
                    <li key={i} className="text-xs text-ink-mute flex items-start gap-2.5 leading-relaxed">
                      <Check size={14} weight="bold" className="text-brand-red mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => onChoose(key)}
                  data-testid={`pricing-choose-${key}`}
                  className={`mt-auto w-full py-4 text-xs font-bold tracking-[0.25em] uppercase transition-colors btn-glitch ${
                    key === "intermediate"
                      ? "bg-brand-red text-black hover:bg-white clip-arrow-r"
                      : key === "pro"
                      ? "bg-brand-purple text-black hover:bg-white clip-arrow-r"
                      : "border border-zinc-700 text-white hover:border-brand-red hover:text-brand-red"
                  }`}
                >
                  {user ? "SUBSCRIBE ▸" : "GET ACCESS ▸"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center text-[10px] tracking-[0.3em] uppercase text-ink-dim">
          ALL TIERS · COMMERCIAL ROYALTY-FREE · FL STUDIO · VST3 · AU · WIN + macOS
        </div>
      </div>
    </div>
  );
}
